import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, List } from 'antd';
import type { FC } from 'react';
import React, { useMemo, useState } from 'react';

import { useRoomCategories } from 'app/hooks/data/rooms/useRoomCategories';
import type { Room } from 'app/typings';

import './RoomCategories.scss';
import { RoomModal } from './RoomModal/RoomModal';

export const RoomCategories: FC<Props> = ({
  rooms,
  saveRoom,
  deleteRoom,
  hotelId,
}) => {
  const [selectedRoom, setSelectedRoom] = useState<Room | {} | null>(null);
  const { data: roomCategories = [] } = useRoomCategories();

  const categories: Record<number, string> = useMemo(
    () =>
      roomCategories.reduce(
        (acc, val) => ({
          ...acc,
          [val.id]: val.name,
        }),
        {}
      ),
    [roomCategories]
  );

  const getActions = (room: Room) => [
    <EditOutlined onClick={() => setSelectedRoom(room)} />,
    <DeleteOutlined onClick={() => deleteRoom(room.id)} />,
  ];

  const getAvatar = (room: Room) => {
    const firstPicture = room.pictures?.[0];

    if (!firstPicture) {
      return null;
    }

    return (
      <div className="avatar">
        <img
          className="twic pic"
          data-src={`image:pictures/${firstPicture.pictureId}`}
          src="https://staycation.twic.pics/v1/placeholder:transparent"
          alt=""
        />
      </div>
    );
  };

  const sortedRooms = rooms.sort((r1, r2) => r1.id - r2.id);

  return (
    <div className="room-categories">
      <div className="categories">
        <List
          itemLayout="horizontal"
          dataSource={sortedRooms}
          renderItem={(room) => (
            <List.Item actions={getActions(room)}>
              <List.Item.Meta
                avatar={getAvatar(room)}
                title={room.categoryId ? categories[room.categoryId] : ''}
                description={room.name}
              />
            </List.Item>
          )}
        />
      </div>
      <div className="footer">
        <Button icon={<PlusOutlined />} onClick={() => setSelectedRoom({})}>
          Add a category
        </Button>
      </div>
      {!!selectedRoom && (
        <RoomModal
          saveRoom={saveRoom}
          close={() => setSelectedRoom(null)}
          room={selectedRoom}
          categories={categories}
          hotelId={hotelId}
        />
      )}
    </div>
  );
};

type Props = {
  rooms: Room[];
  saveRoom: (room: Room) => void;
  deleteRoom: (roomId: number) => void;
  hotelId: number;
};
